import React, { useState, useEffect, useRef } from 'react';
import { Plus, Calendar, Search, Building, Tent, Users, Check, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

export default function AddGigForm({ user, bandId, venues, festivals, loading, onAddGig }) {
  // Location type toggle
  const [locationType, setLocationType] = useState('venue');

  // Form state
  const [newGigVenue, setNewGigVenue] = useState('');
  const [newGigDate, setNewGigDate] = useState('');
  const [newGigWebsite, setNewGigWebsite] = useState('');
  const [newGigContactPerson, setNewGigContactPerson] = useState('');
  const [newGigContactEmail, setNewGigContactEmail] = useState('');
  const [newGigContactPhone, setNewGigContactPhone] = useState('');
  const [newGigAddress, setNewGigAddress] = useState('');
  const [newGigCity, setNewGigCity] = useState('');
  const [newGigRegion, setNewGigRegion] = useState('');
  const [newGigCapacity, setNewGigCapacity] = useState('');
  const [newGigPeriod, setNewGigPeriod] = useState('');
  const [newGigBands, setNewGigBands] = useState([{ name: '', website: '', email: '' }]);

  // Location search state
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isNewLocation, setIsNewLocation] = useState(false);
  const locationInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // UI state
  const [expanded, setExpanded] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          locationInputRef.current && !locationInputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSearchSource = () => locationType === 'venue' ? venues : festivals;

  const handleLocationSearch = (query) => {
    setNewGigVenue(query);
    setSelectedLocation(null);
    setIsNewLocation(false);
    if (query.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    const source = getSearchSource();
    const searchLower = query.toLowerCase();
    const results = source
      .filter(item => item.name?.toLowerCase().includes(searchLower) || item.city?.toLowerCase().includes(searchLower))
      .slice(0, 8);
    setSearchResults(results);
    setShowDropdown(true);
  };

  const selectLocation = (location) => {
    setSelectedLocation(location);
    setNewGigVenue(location.name);
    setIsNewLocation(false);
    setNewGigWebsite(location.website || '');
    setNewGigContactPerson(location.contactPerson || '');
    setNewGigContactEmail(location.email || '');
    setNewGigContactPhone(location.phone || '');
    setNewGigAddress(location.address || '');
    setNewGigCity(location.city || '');
    setNewGigRegion(location.region || '');
    setNewGigCapacity(location.capacity?.toString() || location.estimatedVisitors?.toString() || '');
    setNewGigPeriod(location.period || '');
    setShowDropdown(false);
    setSearchResults([]);
  };

  const markAsNewLocation = () => {
    setIsNewLocation(true);
    setSelectedLocation(null);
    setShowDropdown(false);
  };

  const handleLocationTypeChange = (type) => {
    setLocationType(type);
    setNewGigVenue(''); setSelectedLocation(null); setIsNewLocation(false);
    setSearchResults([]); setShowDropdown(false);
    setNewGigWebsite(''); setNewGigContactPerson(''); setNewGigContactEmail('');
    setNewGigContactPhone(''); setNewGigAddress(''); setNewGigCity('');
    setNewGigRegion(''); setNewGigCapacity(''); setNewGigPeriod('');
  };

  const checkEnrichmentOpportunities = () => {
    if (!selectedLocation) return [];
    const opportunities = [];
    if (newGigContactPerson && !selectedLocation.contactPerson)
      opportunities.push({ field: 'contactPerson', label: 'Contact Person', value: newGigContactPerson });
    if (newGigContactEmail && selectedLocation.email !== newGigContactEmail)
      opportunities.push({ field: 'email', label: 'Email', value: newGigContactEmail, isUpdate: !!selectedLocation.email });
    if (newGigContactPhone && selectedLocation.phone !== newGigContactPhone)
      opportunities.push({ field: 'phone', label: 'Phone', value: newGigContactPhone, isUpdate: !!selectedLocation.phone });
    if (newGigWebsite && !selectedLocation.website)
      opportunities.push({ field: 'website', label: 'Website', value: newGigWebsite });
    if (newGigAddress && !selectedLocation.address)
      opportunities.push({ field: 'address', label: 'Address', value: newGigAddress });
    return opportunities;
  };

  const resetForm = () => {
    setNewGigVenue(''); setNewGigDate(''); setNewGigWebsite('');
    setNewGigContactPerson(''); setNewGigContactEmail(''); setNewGigContactPhone('');
    setNewGigAddress(''); setNewGigCity(''); setNewGigRegion('');
    setNewGigCapacity(''); setNewGigPeriod('');
    setNewGigBands([{ name: '', website: '', email: '' }]);
    setSelectedLocation(null); setIsNewLocation(false); setExpanded(false);
  };

  const handleSubmit = async () => {
    if (!newGigVenue.trim() || !newGigDate || !user || !bandId) return;

    const gigData = {
      venue: newGigVenue,
      venueId: locationType === 'venue' ? (selectedLocation?.id || null) : null,
      festivalId: locationType === 'festival' ? (selectedLocation?.id || null) : null,
      locationType,
      date: newGigDate,
      website: newGigWebsite,
      contactPerson: newGigContactPerson,
      contactEmail: newGigContactEmail,
      contactPhone: newGigContactPhone,
      address: newGigAddress,
      city: newGigCity,
      region: newGigRegion,
      capacity: newGigCapacity ? parseInt(newGigCapacity) : null,
      period: locationType === 'festival' ? newGigPeriod : null,
      bands: newGigBands.filter(band => band.name.trim() !== ''),
      setlist: null, photos: {},
      isNewLocation,
      createdAt: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.displayName || user.email
    };

    const enrichment = checkEnrichmentOpportunities();
    await onAddGig(gigData, selectedLocation, locationType, isNewLocation, enrichment);
    resetForm();
  };

  // ── Shared input className ────────────────────────────────
  const inputCls = "w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand text-sm rounded-pill";

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="bg-brand p-2 rounded-full">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <p className="font-semibold text-lg">Add New Gig</p>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-text-subtle" /> : <ChevronDown className="w-5 h-5 text-text-subtle" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <div className="space-y-4">

            {/* Location Type Toggle */}
            <div>
              <label className="text-xs font-semibold text-text-subtle mb-2 block">Location Type</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleLocationTypeChange('venue')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-pill text-sm font-semibold transition ${
                    locationType === 'venue'
                      ? 'bg-brand text-white'
                      : 'bg-white/10 text-text-muted hover:bg-white/20'
                  }`}
                >
                  <Building className="w-4 h-4" />
                  Venue
                </button>
                <button
                  onClick={() => handleLocationTypeChange('festival')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-pill text-sm font-semibold transition ${
                    locationType === 'festival'
                      ? 'bg-orange-500 text-white'
                      : 'bg-white/10 text-text-muted hover:bg-white/20'
                  }`}
                >
                  <Tent className="w-4 h-4" />
                  Festival
                </button>
              </div>
            </div>

            {/* Location Search */}
            <div className="relative">
              <label className="text-xs font-semibold text-text-subtle mb-1 block">
                {locationType === 'venue' ? 'Venue Name' : 'Festival Name'} *
              </label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-subtle" />
                <input
                  ref={locationInputRef}
                  type="text"
                  value={newGigVenue}
                  onChange={(e) => handleLocationSearch(e.target.value)}
                  onFocus={() => newGigVenue.length >= 2 && setShowDropdown(true)}
                  placeholder={`Search ${locationType}s...`}
                  className="w-full bg-transparent border border-surface-border-light pl-10 pr-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand text-sm rounded-pill"
                />
              </div>

              {/* Location Status Badge */}
              {selectedLocation && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${
                    locationType === 'festival'
                      ? 'bg-orange-500/20 text-orange-400'
                      : 'bg-green-500/20 text-green-400'
                  }`}>
                    <Check className="w-3 h-3" /> From database
                  </span>
                  <span className="text-xs text-text-subtle">{selectedLocation.city}</span>
                  {selectedLocation.period && (
                    <span className="bg-white/10 text-text-muted px-2 py-0.5 rounded-full text-xs">{selectedLocation.period}</span>
                  )}
                  {selectedLocation.genres?.slice(0, 2).map((g, i) => (
                    <span key={i} className="bg-white/10 text-text-muted px-2 py-0.5 rounded-full text-xs">{g}</span>
                  ))}
                </div>
              )}
              {isNewLocation && (
                <div className="mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 inline-flex ${
                    locationType === 'festival'
                      ? 'bg-orange-500/20 text-orange-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    <Plus className="w-3 h-3" /> New {locationType} (will be added to database)
                  </span>
                </div>
              )}

              {/* Location Dropdown */}
              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute z-20 w-full mt-1 card-3d border border-surface-border-light rounded-card shadow-xl max-h-64 overflow-y-auto"
                >
                  {searchResults.length > 0 ? (
                    <>
                      {searchResults.map(item => (
                        <button
                          key={item.id}
                          onClick={() => selectLocation(item)}
                          className="w-full px-4 py-3 hover:bg-white/10 transition text-left border-b border-surface-border-light last:border-0"
                        >
                          <div className="flex items-start gap-3">
                            {locationType === 'festival' ? (
                              <Tent className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Building className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-white truncate">{item.name}</p>
                              <p className="text-xs text-text-subtle flex flex-wrap items-center gap-2">
                                <span>{item.city || 'Unknown location'}</span>
                                {item.period && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" /> {item.period}
                                  </span>
                                )}
                                {(item.capacity || item.estimatedVisitors) && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" /> {(item.capacity || item.estimatedVisitors).toLocaleString()}
                                  </span>
                                )}
                              </p>
                              {item.genres?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.genres.slice(0, 3).map((g, i) => (
                                    <span key={i} className="bg-white/10 text-text-muted px-1.5 py-0.5 rounded-full text-xs">{g}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                      <button
                        onClick={markAsNewLocation}
                        className="w-full px-4 py-3 hover:bg-white/10 transition text-left bg-white/5"
                      >
                        <div className={`flex items-center gap-2 ${locationType === 'festival' ? 'text-orange-400' : 'text-brand'}`}>
                          <Plus className="w-4 h-4" />
                          <span className="text-sm font-semibold">Add "{newGigVenue}" as new {locationType}</span>
                        </div>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={markAsNewLocation}
                      className="w-full px-4 py-3 hover:bg-white/10 transition text-left"
                    >
                      <div className={`flex items-center gap-2 ${locationType === 'festival' ? 'text-orange-400' : 'text-brand'}`}>
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-semibold">Add "{newGigVenue}" as new {locationType}</span>
                      </div>
                      <p className="text-xs text-text-subtle ml-6">Not found in database</p>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="text-xs font-semibold text-text-subtle mb-1 block">Date *</label>
              <button
                type="button"
                onClick={() => document.getElementById('gig-date-picker').showPicker()}
                className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white text-sm flex items-center gap-2 hover:bg-white/5 transition cursor-pointer focus:outline-none focus:border-brand rounded-pill"
              >
                <Calendar className="w-4 h-4 text-white flex-shrink-0" />
                <span className="flex-1 text-left">
                  {newGigDate ? new Date(newGigDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Select date...'}
                </span>
              </button>
              <input
                id="gig-date-picker"
                type="date"
                value={newGigDate}
                onChange={(e) => setNewGigDate(e.target.value)}
                className="hidden"
              />
            </div>

            {/* Festival Period */}
            {locationType === 'festival' && (
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">Festival Period</label>
                <input
                  type="text"
                  value={newGigPeriod}
                  onChange={(e) => setNewGigPeriod(e.target.value)}
                  placeholder="e.g., juli, augustus weekend 2..."
                  className={inputCls}
                />
              </div>
            )}

            {/* Location Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">City</label>
                <input type="text" value={newGigCity} onChange={(e) => setNewGigCity(e.target.value)} placeholder="City..." className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">Region</label>
                <input type="text" value={newGigRegion} onChange={(e) => setNewGigRegion(e.target.value)} placeholder="Region..." className={inputCls} />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-text-subtle mb-1 block">Address</label>
              <input type="text" value={newGigAddress} onChange={(e) => setNewGigAddress(e.target.value)} placeholder="Street address..." className={inputCls} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">Website</label>
                <input type="url" value={newGigWebsite} onChange={(e) => setNewGigWebsite(e.target.value)} placeholder="https://..." className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">
                  {locationType === 'festival' ? 'Expected Visitors' : 'Capacity'}
                </label>
                <input
                  type="number"
                  value={newGigCapacity}
                  onChange={(e) => setNewGigCapacity(e.target.value)}
                  placeholder={locationType === 'festival' ? 'e.g., 50000' : 'e.g., 500'}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">Contact Person</label>
                <input type="text" value={newGigContactPerson} onChange={(e) => setNewGigContactPerson(e.target.value)} placeholder="Name..." className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">Email</label>
                <input type="email" value={newGigContactEmail} onChange={(e) => setNewGigContactEmail(e.target.value)} placeholder="email@..." className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-1 block">Phone</label>
                <input type="tel" value={newGigContactPhone} onChange={(e) => setNewGigContactPhone(e.target.value)} placeholder="06..." className={inputCls} />
              </div>
            </div>

            {/* Other Bands */}
            <div>
              <label className="text-xs font-semibold text-text-subtle mb-1 block">Other Bands on the Bill</label>
              {newGigBands.map((band, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 mb-2">
                  <input
                    type="text"
                    value={band.name}
                    onChange={(e) => {
                      const updated = [...newGigBands];
                      updated[index].name = e.target.value;
                      setNewGigBands(updated);
                    }}
                    placeholder="Band name..."
                    className={`flex-1 ${inputCls}`}
                  />
                  <input
                    type="url"
                    value={band.website}
                    onChange={(e) => {
                      const updated = [...newGigBands];
                      updated[index].website = e.target.value;
                      setNewGigBands(updated);
                    }}
                    placeholder="Website (optional)"
                    className={`flex-1 ${inputCls}`}
                  />
                  {newGigBands.length > 1 && (
                    <button
                      onClick={() => setNewGigBands(newGigBands.filter((_, i) => i !== index))}
                      className="bg-transparent hover:bg-red-600 transition p-2 flex-shrink-0 rounded-full"
                    >
                      <Trash2 className="w-4 h-4 text-text-subtle" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setNewGigBands([...newGigBands, { name: '', website: '', email: '' }])}
                className="text-sm text-brand hover:text-brand/80 flex items-center gap-1 mt-2"
              >
                <Plus className="w-4 h-4" /> Add another band
              </button>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!newGigVenue.trim() || !newGigDate || loading}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition h-btn font-semibold flex items-center justify-center gap-2 rounded-pill"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add Gig
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
